// What a failed getUserMedia for the call's MICROPHONE means for the person (web client + the Windows
// call host, which runs this same CallManager in a hidden WebView2).
//
// Before 2026-10-14 every failure read «Las llamadas necesitan permiso del micrófono.», including a
// PC with no microphone at all — a Windows tester on a desktop PC looked for a permission that did not
// exist (Windows 1.0.3 report). The DOMException names are the spec's (Media Capture and Streams
// §getUserMedia) plus Chromium's legacy aliases:
//   NotAllowedError / PermissionDeniedError / SecurityError → the page or the OS said no;
//   NotFoundError / DevicesNotFoundError / OverconstrainedError → there is no microphone;
//   NotReadableError / TrackStartError / AbortError → there is one, but it could not be opened (another
//     app holds it, or — on Windows — the OS privacy switch blocks the browser engine's process).
// A missing microphone is NOT a refusal: Mattermost's own calls client lets you join and listen, and so
// do we (the call starts muted, «Activar micro» tries again — a headset plugged in later just works).

/** Why the microphone could not be used. */
export type MicFailure = 'denied' | 'missing' | 'unavailable';

/** The outcome of asking for the microphone at JOIN: go ahead, go ahead listening only, or refuse. */
export type MicReadiness = 'granted' | MicFailure;

const DENIED = new Set(['NotAllowedError', 'PermissionDeniedError', 'SecurityError']);
const MISSING = new Set(['NotFoundError', 'DevicesNotFoundError', 'OverconstrainedError', 'ConstraintNotSatisfiedError']);

/** The DOMException's name (or an error-like object's), '' when there is none. */
function errorName(error: unknown): string {
  if (error && typeof error === 'object' && 'name' in error) {
    const name = (error as { name: unknown }).name;
    if (typeof name === 'string') return name;
  }
  return '';
}

/** Classifies a getUserMedia rejection. Anything unrecognised is "could not be opened", never "denied". */
export function micFailure(error: unknown): MicFailure {
  const name = errorName(error);
  if (DENIED.has(name)) return 'denied';
  if (MISSING.has(name)) return 'missing';
  return 'unavailable';
}

/** Whether a call may go ahead with this answer (a missing microphone joins listening only). */
export function joinsWith(readiness: MicReadiness): boolean {
  return readiness === 'granted' || readiness === 'missing';
}

/** No audio input in an enumerateDevices() answer (labels are not needed to count them). */
export function hasNoAudioInput(devices: ReadonlyArray<{ kind: string }>): boolean {
  return !devices.some((d) => d.kind === 'audioinput');
}
